import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Card } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    EXPENSE_SUGGESTION_KEYS,
    EXPENSE_SUGGESTION_LABEL_KEY,
} from '../manualImportMeta';
import type { ManualImportForm } from '../useManualImport';
import { ExpenseRow } from './ExpenseRow';

const EXPENSE_SUGGESTIONS_ID = 'manual-import-expense-suggestions';

type ExpensesSectionProps = {
    form: ManualImportForm;
};

/** Extra import costs (transport, customs, …) that feed the cost summary. */
export const ExpensesSection: React.FC<ExpensesSectionProps> = ({ form }) => {
    const { t } = useTranslation('pages');

    return (
        <section {...getNodeTestIdProp('manual-import-expenses')}>
            <Card {...getNodeTestIdProp('manual-import-expenses-card')}>
                <Card.Header
                    className="flex flex-row flex-wrap items-center justify-between gap-3"
                    {...getNodeTestIdProp('manual-import-expenses-header')}
                >
                    <Card.Title
                        {...getNodeTestIdProp('manual-import-expenses-title')}
                    >
                        {t('purchases.manualImport.extraExpenses')}
                    </Card.Title>
                    <Button
                        onPress={form.onAddExpense}
                        size="sm"
                        variant="tertiary"
                        {...getNodeTestIdProp('manual-import-add-expense')}
                    >
                        <Icon icon="gravity-ui:plus" />
                        {t('purchases.manualImport.addExpense')}
                    </Button>
                </Card.Header>
                <Card.Content
                    className="flex flex-col gap-3"
                    {...getNodeTestIdProp('manual-import-expenses-content')}
                >
                    {form.state.expenses.length === 0 ? (
                        <p
                            className="py-2 text-sm text-muted"
                            {...getNodeTestIdProp(
                                'manual-import-expenses-empty',
                            )}
                        >
                            {t('purchases.manualImport.noExpenses')}
                        </p>
                    ) : (
                        form.state.expenses.map((expense) => (
                            <ExpenseRow
                                expense={expense}
                                form={form}
                                key={expense.key}
                                suggestionsId={EXPENSE_SUGGESTIONS_ID}
                            />
                        ))
                    )}

                    <datalist id={EXPENSE_SUGGESTIONS_ID}>
                        {EXPENSE_SUGGESTION_KEYS.map((key) => (
                            <option
                                key={key}
                                value={t(EXPENSE_SUGGESTION_LABEL_KEY[key])}
                            />
                        ))}
                    </datalist>
                </Card.Content>
            </Card>
        </section>
    );
};
